/* eslint-disable @atlaskit/design-system/no-html-button */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useEffect, useMemo, useRef } from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { css, jsx, cssMap } from '@atlaskit/css';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import Enter from '../../assets/Enter';
import { LogoSvg } from '../../assets/Logo';
import { messages } from './messages';
import type { Suggestion } from './types';

const contentStyles = cssMap({
	default: {
		hyphens: 'auto',
		wordWrap: 'break-word',
		flex: 1,
		minWidth: '90px',
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
	},
});
const chevronIconStyles = css({
	paddingTop: token('space.0'),
	paddingBottom: token('space.0'),
	paddingLeft: token('space.050'),
	paddingRight: token('space.050'),
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const descriptionStylesUnbounded = cssUnbounded({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '14px',
});

const descriptionStyles = css({
	hyphens: 'auto',
	wordWrap: 'break-word',
	flex: 1,
	minWidth: '90px',
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	font: token('font.body.small'),
	color: token('color.text.subtlest'),
	display: 'inline-block',
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const enterStylesUnbounded = cssUnbounded({
	// with '&', the following selector applies only if this icon style is under a highlighted button
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	"button[data-highlighted='true'] &": {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	borderRadius: token('radius.small', '3px'),
});

const enterStyles = css({
	display: 'none',
	height: '20px',
	width: '20px',
	color: token('color.text.subtle'),
	backgroundColor: token('color.background.neutral.subtle.hovered'),
});

const statusStyles = cssMap({
	default: {
		paddingLeft: token('space.050'),
	},
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const suggestionButtonStylesUnbounded = cssUnbounded({
	border: 0,
	borderRadius: 0,
	borderTopLeftRadius: 0,
	borderTopRightRadius: 0,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	gap: '13px',
	background: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:not([disabled])': {
		cursor: 'pointer',
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"&[data-highlighted='true']": {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'&:not(:active)': {
				backgroundColor: token('color.background.neutral.subtle.hovered'),
			},
		},
		'&:focus': {
			outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
			outlineOffset: token('space.negative.025'),
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&[disabled]': {
		backgroundColor: token('color.background.disabled'),
		color: token('color.text.disabled'),
		cursor: 'not-allowed',
	},
});

const suggestionButtonStyles = css({
	width: '100%',
	minHeight: '40px',
	paddingTop: token('space.075'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.075'),
	paddingLeft: token('space.150'),
	marginTop: token('space.0'),
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'start',
	alignItems: 'center',
	textAlign: 'left',
	font: token('font.body'),
	fontWeight: token('font.weight.regular'),
	color: token('color.text'),
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const suggestionItemStylesUnbounded = cssUnbounded({
	border: 0,
	minHeight: token('space.500'),
	background: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:not([disabled])': {
		cursor: 'pointer',
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"&[data-highlighted='true']": {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'&:not(:active)': {
				backgroundColor: token('color.background.neutral.subtle.hovered'),
			},
		},
		'&:focus': {
			outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
			outlineOffset: token('space.negative.025'),
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&[disabled]': {
		backgroundColor: token('color.background.disabled'),
		color: token('color.text.disabled'),
		cursor: 'not-allowed',
	},
});

const suggestionItemStyles = css({
	width: '100%',
	paddingLeft: token('space.250'),
	paddingRight: token('space.250'),
	paddingTop: token('space.075'),
	paddingBottom: token('space.075'),
	marginTop: token('space.0'),
	marginBottom: token('space.0'),
	marginLeft: token('space.0'),
	marginRight: token('space.0'),
	display: 'flex',
	justifyContent: 'start',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	gap: token('space.150'),
	textAlign: 'left',
	fontWeight: token('font.weight.regular'),
	color: token('color.text'),
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const suggestionIconStylesUnbounded = cssUnbounded({
	// Increase the size of default icon a little so it looks better
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > .default-icon': {
		height: '24px',
		width: '20px',
	},
});

const suggestionIconStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: '24px',
	width: '24px',
});

const suggestionListItemStyles = css({
	marginTop: token('space.0'),
	listStyle: 'none',
});

const tellAILabelStyles = css({
	fontWeight: token('font.weight.medium'),
	marginRight: token('space.050'),
});

const rovoSuggestionButtonStyles = css({
	minHeight: '46px',
});
const rovoSuggestionIconStyles = css({
	height: '32px',
	width: '32px',
});

// Height of ListHeading component, used to calculate the scroll position.
// Kind of a hack, but it's the easiest way to get the height of the sticky group header.
const STICKY_GROUP_HEADER_HEIGHT = 32;

const ensureElementIsVisibleInScrollContainer = (
	listItem: HTMLElement,
	stickyHeaderHeight: number,
) => {
	// ensure the structure of suggestions li element > ul (position relative) > scrollable div (overflow auto)
	const scrollContainer = listItem.offsetParent?.parentElement;
	if (!scrollContainer || scrollContainer.tagName !== 'DIV') {
		return;
	}

	const scrollContainerRect = scrollContainer.getBoundingClientRect();
	const itemRect = listItem.getBoundingClientRect();

	// if highlighted item is above/below the current scroll viewport
	const itemNotVisible =
		listItem.offsetTop < scrollContainer.scrollTop ||
		listItem.offsetTop + itemRect.height > scrollContainer.scrollTop + scrollContainerRect.height;

	if (itemNotVisible) {
		// Can't use item.scrollIntoView() because it moves the whole page too
		scrollContainer.scrollTo({
			top: listItem.offsetTop - stickyHeaderHeight,
			behavior: 'smooth',
		});
	}
};
const statusLozengeMessages: Record<
	Exclude<Suggestion['statusLozengeType'], undefined>,
	MessageDescriptor
> = {
	beta: messages.suggestionStatusBeta,
	new: messages.suggestionStatusNew,
};

type ListItemProps = {
	customLabel?: string;
	hasGroupHeading?: boolean;
	highlighted: boolean;
	setSuggestion?: (suggestion: Suggestion, eventName: 'focus' | 'mousemove') => void;
	suggestion: Suggestion;
};

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const suggestionIconContainerStylesUnbounded = cssUnbounded({
	height: token('space.400'),
	width: token('space.400'),
});

const suggestionIconContainerStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const suggestionIconContainerBorderStyles = css({
	borderWidth: token('border.width'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.small'),
});

export const ListItem = ({
	suggestion,
	customLabel,
	highlighted,
	setSuggestion,
	hasGroupHeading,
}: ListItemProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const listItemRef = useRef<HTMLLIElement>(null);
	const hasChildren = useMemo(() => {
		return suggestion.childSuggestions && suggestion.childSuggestions.length;
	}, [suggestion.childSuggestions]);

	const updateSuggestion = React.useCallback(
		(eventName: 'focus' | 'mousemove') => {
			if (setSuggestion) {
				setSuggestion(suggestion, eventName);
			}
		},
		[setSuggestion, suggestion],
	);

	const onFocus = React.useCallback(() => {
		updateSuggestion('focus');
		suggestion.onSuggestionFocused();
	}, [updateSuggestion, suggestion]);

	const onMouseMove = React.useCallback(() => {
		updateSuggestion('mousemove');
	}, [updateSuggestion]);

	useEffect(() => {
		// If the item is highlighted and not hovered, scroll it into view
		if (highlighted && listItemRef.current && !listItemRef.current.matches(':hover')) {
			ensureElementIsVisibleInScrollContainer(
				listItemRef.current,
				hasGroupHeading ? STICKY_GROUP_HEADER_HEIGHT : 0,
			);
		}
	}, [listItemRef, highlighted, hasGroupHeading]);

	const chevronRight = useMemo(() => {
		if (hasChildren && !highlighted) {
			return (
				<span css={chevronIconStyles}>
					<ChevronRightIcon label="" color={token('color.icon.subtle')} size="small" />
				</span>
			);
		}

		return null;
	}, [hasChildren, highlighted]);

	const statusLozengeText =
		suggestion.statusLozengeType &&
		(statusLozengeMessages[suggestion.statusLozengeType]
			? formatMessage(statusLozengeMessages[suggestion.statusLozengeType])
			: null);

	const ariaLabel = customLabel
		? customLabel
		: typeof suggestion.label === 'string'
			? suggestion.label
			: formatMessage(messages.tellAILabel);

	return (
		<li css={suggestionListItemStyles} ref={listItemRef}>
			<button
				css={[
					suggestionButtonStyles,
					suggestionButtonStylesUnbounded,
					fg('ai-smart-button-team-25') && suggestionItemStyles,
					fg('ai-smart-button-team-25') && suggestionItemStylesUnbounded,
					suggestion.value.includes('agent:') && rovoSuggestionButtonStyles,
				]}
				type="button"
				onClick={suggestion.onSelect}
				onMouseMove={onMouseMove}
				data-testid={suggestion.testId || (customLabel ? `tell-ai` : suggestion.label)}
				data-highlighted={highlighted}
				aria-label={ariaLabel}
				onFocus={onFocus}
			>
				{!suggestion.hideIcon && (
					<span
						css={[
							suggestionIconStyles,
							suggestionIconStylesUnbounded,
							fg('ai-smart-button-team-25') && suggestionIconContainerStyles,
							fg('ai-smart-button-team-25') && suggestionIconContainerStylesUnbounded,
							!suggestion.isAgent &&
								fg('ai-smart-button-team-25') &&
								suggestionIconContainerBorderStyles,
							suggestion.value.includes('agent:') && rovoSuggestionIconStyles,
						]}
					>
						{suggestion.icon ||
							(fg('ai-smart-button-team-25') ? (
								<AtlassianIntelligenceIcon spacing="spacious" label="" />
							) : (
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								<LogoSvg className="default-icon" alt={''} />
							))}
					</span>
				)}
				<Flex direction="column" xcss={contentStyles.default}>
					<Flex alignItems="center">
						{!customLabel ? (
							<Text>{suggestion.label}</Text>
						) : (
							<React.Fragment>
								<span css={tellAILabelStyles}>{customLabel}</span> "{suggestion.label}"
							</React.Fragment>
						)}
						{statusLozengeText && (
							<Flex alignItems="center" xcss={statusStyles.default}>
								<Lozenge appearance="discovery">{statusLozengeText}</Lozenge>
							</Flex>
						)}
					</Flex>
					{suggestion.description && (
						<span css={[descriptionStyles, descriptionStylesUnbounded]}>
							{suggestion.description}
						</span>
					)}
				</Flex>

				{suggestion.rightText && <Text color="color.text.subtlest">{suggestion.rightText}</Text>}

				{suggestion.value !== 'no-suggestions' && (
					<span css={[enterStyles, enterStylesUnbounded]}>
						<Enter />
					</span>
				)}

				{chevronRight}
			</button>
		</li>
	);
};
